import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  SlackLogoIcon,
  TrophyIcon,
  ArrowClockwiseIcon,
  CheckCircleIcon,
  PaperPlaneTiltIcon,
} from "@phosphor-icons/react";
import { DecisionBadge } from "./DecisionBadge";

const ring = (pct) => {
  if (pct >= 90) return "#10b981";
  if (pct >= 75) return "#f59e0b";
  return "#e11d48";
};

export const SummaryReport = ({ summary }) => {
  const color = ring(summary.accuracy);
  const circ = 2 * Math.PI * 52;
  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-[1100px] mx-auto px-6 md:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="label mb-2">Module Complete</div>
          <h1 className="font-head font-black text-4xl md:text-5xl tracking-tighter">
            {summary.module_title}
          </h1>
          <p className="text-[#525252] mt-2">
            Report for <span className="font-semibold text-[#0a0a0a]">{summary.trainee_name}</span> · {summary.payer}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[340px_1fr] gap-6">
          {/* Accuracy dial */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="border-2 border-[#0a0a0a] bg-white p-6 flex flex-col items-center"
            data-testid="qa-accuracy-card"
          >
            <div className="label mb-4">QA Accuracy</div>
            <div className="relative w-[140px] h-[140px]">
              <svg width="140" height="140" className="-rotate-90">
                <circle cx="70" cy="70" r="52" fill="none" stroke="#f1f3f5" strokeWidth="12" />
                <motion.circle
                  cx="70" cy="70" r="52" fill="none" stroke={color} strokeWidth="12"
                  strokeLinecap="butt"
                  strokeDasharray={circ}
                  initial={{ strokeDashoffset: circ }}
                  animate={{ strokeDashoffset: circ - (circ * summary.accuracy) / 100 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-head font-black text-3xl tracking-tighter" data-testid="accuracy-value">
                  {summary.accuracy}%
                </span>
                <span className="text-xs text-[#525252] font-semibold">
                  {summary.correct}/{summary.total} correct
                </span>
              </div>
            </div>
            <Link
              to="/leaderboard"
              data-testid="view-leaderboard-btn"
              className="mt-6 w-full bg-[#0a0a0a] text-white font-semibold py-3 flex items-center justify-center gap-2 hover:bg-[#262626] transition-colors"
            >
              <TrophyIcon size={17} weight="bold" /> View Leaderboard
            </Link>
            <Link
              to="/"
              data-testid="new-session-btn"
              className="mt-3 w-full border border-[#0a0a0a] font-semibold py-3 flex items-center justify-center gap-2 hover:bg-[#f8f9fa] transition-colors"
            >
              <ArrowClockwiseIcon size={17} weight="bold" /> Start Another Module
            </Link>
          </motion.div>

          <div className="space-y-6">
            {/* Slack report preview */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="border border-[#e5e5e5] bg-white"
              data-testid="slack-report-preview"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e5e5] bg-[#f8f9fa]">
                <div className="flex items-center gap-2">
                  <SlackLogoIcon size={18} weight="fill" color="#4A154B" />
                  <span className="label text-[0.62rem]">
                    Report sent to {summary.slack_report.channel}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 text-[0.65rem] font-bold text-[#10b981]">
                  <PaperPlaneTiltIcon size={13} weight="bold" /> DELIVERED
                </span>
              </div>
              <div className="p-4 flex gap-3">
                <div className="h-9 w-9 bg-[#4A154B] flex items-center justify-center shrink-0 rounded-sm">
                  <span className="text-white font-head font-black text-xs">QA</span>
                </div>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-sm">Claims QA Bot</span>
                    <span className="text-[0.65rem] text-[#a3a3a3]">now</span>
                  </div>
                  <pre className="mt-1 whitespace-pre-wrap font-mono text-[0.8rem] leading-relaxed text-[#171717]">
{summary.slack_report.text}
                  </pre>
                </div>
              </div>
            </motion.div>

            {/* Mistakes review */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18 }}
              className="border border-[#e5e5e5] bg-white"
            >
              <div className="px-4 py-3 border-b border-[#e5e5e5] label text-[0.62rem]">
                {summary.mistakes.length > 0
                  ? `Claims to Review (${summary.mistakes.length})`
                  : "Perfect Run"}
              </div>
              {summary.mistakes.length === 0 ? (
                <div className="p-6 flex items-center gap-3 text-[#10b981]">
                  <CheckCircleIcon size={24} weight="fill" />
                  <span className="font-semibold text-[#171717]">
                    No compliance errors — every determination was correct.
                  </span>
                </div>
              ) : (
                <div className="divide-y divide-[#e5e5e5]">
                  {summary.mistakes.map((m, i) => (
                    <div key={m.claim_number} className="p-4 flex items-center justify-between gap-4" data-testid={`mistake-row-${i}`}>
                      <div className="min-w-0">
                        <div className="font-mono text-sm font-semibold">{m.claim_number}</div>
                        <div className="label text-[0.58rem] mt-1">{m.rule_reference}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <DecisionBadge decision={m.your_decision} />
                        <span className="text-[#a3a3a3] text-xs">→</span>
                        <DecisionBadge decision={m.correct_decision} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
